import React from 'react';
import { Box, Typography, Grid, Paper } from '@mui/material';

// ----- ICONS -----
const StrategyIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
    <circle cx="32" cy="32" r="30" stroke="#2E5CAA" strokeWidth="3" />
    <path d="M22 34L32 24L42 34" stroke="#2E5CAA" strokeWidth="3" strokeLinecap="round" />
    <rect x="28" y="34" width="8" height="12" stroke="#2E5CAA" strokeWidth="3" />
  </svg>
);

const AnalyticsIcon = () => (
  <svg width="64" height="64" fill="none">
    <rect x="12" y="30" width="8" height="20" fill="#2E5CAA" />
    <rect x="28" y="20" width="8" height="30" fill="#9BB5E2" />
    <rect x="44" y="10" width="8" height="40" fill="#2E5CAA" />
    <path d="M16 38L32 28L48 18" stroke="#2E5CAA" strokeWidth="2" />
  </svg>
);

const TechnologyIcon = () => (
  <svg width="64" height="64" fill="none">
    <rect x="14" y="18" width="36" height="28" rx="4" stroke="#2E5CAA" strokeWidth="3" />
    <circle cx="32" cy="32" r="6" stroke="#2E5CAA" strokeWidth="2" />
    <path d="M32 38V50" stroke="#2E5CAA" strokeWidth="2" />
    <path d="M32 18V14" stroke="#2E5CAA" strokeWidth="2" />
  </svg>
);

const NetworkIcon = () => (
  <svg width="64" height="64" fill="none">
    <circle cx="32" cy="32" r="28" stroke="#2E5CAA" strokeWidth="3" />
    <circle cx="32" cy="32" r="8" stroke="#2E5CAA" strokeWidth="2" />
    <circle cx="32" cy="14" r="4" stroke="#2E5CAA" strokeWidth="2" />
    <circle cx="32" cy="50" r="4" stroke="#2E5CAA" strokeWidth="2" />
    <circle cx="50" cy="32" r="4" stroke="#2E5CAA" strokeWidth="2" />
    <circle cx="14" cy="32" r="4" stroke="#2E5CAA" strokeWidth="2" />
    <path d="M32 18V28" stroke="#2E5CAA" strokeWidth="2" />
    <path d="M32 36V46" stroke="#2E5CAA" strokeWidth="2" />
    <path d="M36 32H46" stroke="#2E5CAA" strokeWidth="2" />
    <path d="M28 32H18" stroke="#2E5CAA" strokeWidth="2" />
  </svg>
);

const OperationsIcon = () => (
  <svg width="64" height="64" fill="none">
    <rect x="18" y="22" width="28" height="20" rx="4" stroke="#2E5CAA" strokeWidth="3" />
    <path d="M22 32h20" stroke="#2E5CAA" strokeWidth="2" />
    <circle cx="24" cy="32" r="2" fill="#2E5CAA" />
    <circle cx="40" cy="32" r="2" fill="#2E5CAA" />
  </svg>
);

const InnovationIcon = () => (
  <svg width="64" height="64" fill="none">
    <circle cx="32" cy="32" r="24" stroke="#2E5CAA" strokeWidth="3" />
    <path d="M32 18V32L44 38" stroke="#2E5CAA" strokeWidth="3" />
    <circle cx="32" cy="44" r="3" fill="#2E5CAA" />
  </svg>
);

const RiskIcon = () => (
  <svg width="64" height="64" fill="none">
    <rect x="16" y="22" width="32" height="20" rx="6" stroke="#2E5CAA" strokeWidth="3" />
    <path d="M32 32v6" stroke="#2E5CAA" strokeWidth="2" />
    <circle cx="32" cy="29" r="2" fill="#2E5CAA" />
  </svg>
);

// ----- SERVICES DATA -----
const services = [
  {
    name: 'Strategy Consulting',
    description: 'Helping businesses develop long-term strategic goals and navigate market changes.',
    icon: <StrategyIcon />,
  },
  {
    name: 'Digital and Analytics',
    description: 'Generate insightful reports using real-time data to improve visibility, anticipate vessel arrivals, and uncover opportunities for cost optimization and improved routing with proprietary digital tools.',
    icon: <AnalyticsIcon />,
  },
  {
    name: 'Technology Transformation',
    description: 'Guiding organizations through digital innovation and IT modernization.',
    icon: <TechnologyIcon />,
  },
  {
    name: 'Network Optimization',
    description: 'Enhance location planning, route optimization, and network scheduling to drive long-term cost efficiency.',
    icon: <NetworkIcon />,
  },
  {
    name: 'Operational Improvements',
    description: 'Optimize equipment, workflows, and production sites to enhance quality and boost efficiency through improved service delivery.',
    icon: <OperationsIcon />,
  },
  {
    name: 'Service Innovation',
    description: 'Develop, evaluate, and launch innovative products while upgrading service delivery models to enhance key business capabilities.',
    icon: <InnovationIcon />,
  },
  {
    name: 'Risk Management',
    description: 'Identifying and mitigating risks to protect business assets and reputation.',
    icon: <RiskIcon />,
  },
];

// ----- MAIN COMPONENT -----
const ServicesSection: React.FC = () => {
  return (
    <Box id="services" sx={{ pt: 4, pb: 4, px: 2, textAlign: 'center' }}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 600,
          mb: 4,
          fontFamily: "'Playfair Display SC', Georgia, serif",
        }}
      >
        Our Services
      </Typography>

      <Grid container spacing={7} justifyContent="center">
        {services.map((service, index) => (
          <Grid {...({ item: true, xs: 12, md: 4, lg: 4, xl: 4 } as any)} key={index}>
            <Paper
              elevation={3}
              sx={{
                height: { xs: 'auto', md: 220 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: { xs: 3, md: 3 },
                borderRadius: 0,
              }}
            >
              <Box sx={{ mb: 2 }}>{service.icon}</Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {service.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  maxWidth: 300,
                  px: { xs: 1, md: 0 },
                }}
              >
                {service.description}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ServicesSection;
